/**
 * Asistente IA in-app — cliente del bot de ayuda (Gemini server-side).
 *
 * El backend es stateless: mandamos el historial de texto completo en cada turno.
 * Las acciones de ESCRITURA vuelven como `pending` (tool+args+resumen) para que el
 * usuario confirme; al confirmar reenviamos con `confirm`.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from './api';
import { getLocale } from './i18n';
import type { UsageMeta } from './aiCredits';

/** Acción de escritura propuesta por el bot, a la espera de confirmación. */
export interface PendingAction {
  tool: string;
  args: Record<string, unknown>;
  summary: string;
}

/** Un turno de texto del transcript (lo único que persiste el front). */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  reply: string;
  pending?: PendingAction;
  /** true cuando el turno cerró una acción confirmada. */
  done?: boolean;
  /** Estado de cupo/créditos tras el turno (para el contador del widget). */
  usage?: UsageMeta;
}

export interface AssistantConfig {
  enabled: boolean;
}

/** ¿Se muestra el widget? (toggle del admin + credencial cargada). */
export function useAssistantConfig() {
  return useQuery({
    queryKey: ['assistant', 'config'],
    queryFn: () => api.get<AssistantConfig>('/assistant/config'),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** Envía un turno de chat. `confirm` ejecuta una acción de escritura ya aprobada. */
export function sendAssistantChat(
  messages: ChatMessage[],
  confirm?: { tool: string; args: Record<string, unknown> },
): Promise<ChatResponse> {
  return api.post<ChatResponse>('/assistant/chat', {
    messages,
    confirm,
    locale: getLocale(),
  });
}
